import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Add,
  Delete,
  Search,
  AutoAwesome,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { WorkspaceField, defaultCreateWorkspace } from '../../../components/WorkspaceField';
import { projectsService, ProjectSummary } from '../../../services/projects.service';
import { useStudioStore } from '../stores/studio.store';
import { useCanvasStore } from '../canvas/useCanvasStore';

const SIDEBAR_WIDTH = 260;

interface Props {
  collapsed: boolean;
}

export default function ProjectSidebar({ collapsed }: Props) {
  const navigate = useNavigate();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newWorkspaceId, setNewWorkspaceId] = useState(defaultCreateWorkspace);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const setProject = useStudioStore((s) => s.setProject);
  const clearAssets = useStudioStore((s) => s.clearAssets);

  const { data: projectsData, isLoading } = useQuery({
    queryKey: ['studio-projects'],
    queryFn: () => projectsService.list(0, 100),
  });

  const projects = (projectsData?.data || []).filter((ws: ProjectSummary) =>
    !search || ws.name.toLowerCase().includes(search.toLowerCase())
  );

  const createMutation = useMutation({
    mutationFn: (name: string) => projectsService.create(name, newWorkspaceId),
    onSuccess: (ws) => {
      queryClient.invalidateQueries({ queryKey: ['studio-projects'] });
      setCreateOpen(false);
      setNewName('');
      setNewWorkspaceId(defaultCreateWorkspace());
      navigate(`/studio/${ws.id}`);
    },
    onError: (err: Error) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => projectsService.remove(id),
    onSuccess: (_, id) => {
      if (urlProjectId === id) {
        setProject(null, null);
        clearAssets();
        useCanvasStore.setState({ nodes: [], edges: [] });
        navigate('/studio');
      }
      queryClient.invalidateQueries({ queryKey: ['studio-projects'] });
      setDeleteId(null);
    },
    onError: (err: Error) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return 'now';
    if (diffMin < 60) return `${diffMin}m`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h`;
    const diffD = Math.floor(diffHr / 24);
    if (diffD < 30) return `${diffD}d`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  if (collapsed) return null;

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          fullWidth
          size="small"
          onClick={() => { setNewWorkspaceId(defaultCreateWorkspace()); setCreateOpen(true); }}
          sx={{ textTransform: 'none', mb: 1 }}
        >
          New Project
        </Button>
        <TextField
          size="small"
          placeholder="Search..."
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
        />
      </Box>

      <Divider />

      {/* List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 1.5 }}>
            {[...Array(5)].map((_, i) => <Skeleton key={i} height={48} />)}
          </Box>
        ) : projects.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <AutoAwesome sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {search ? 'No matches' : 'No projects yet'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ px: 0.5, py: 0.5 }}>
            {projects.map((ws: ProjectSummary) => {
              const isActive = ws.id === urlProjectId;
              return (
                <ListItemButton
                  key={ws.id}
                  selected={isActive}
                  onClick={() => navigate(`/studio/${ws.id}`)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.25,
                    py: 0.75,
                    px: 1.5,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { bgcolor: 'primary.dark' },
                      '& .MuiTypography-root': { color: 'inherit' },
                      '& .MuiChip-root': { color: 'inherit', borderColor: 'rgba(255,255,255,0.4)' },
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={isActive ? 600 : 400} noWrap>
                        {ws.name}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.25 }}>
                        <Typography variant="caption" color="inherit" sx={{ opacity: 0.7 }}>
                          {ws.assetCount} assets
                        </Typography>
                        <Typography variant="caption" color="inherit" sx={{ opacity: 0.5 }}>
                          · {formatDate(ws.updatedAt)}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteId(ws.id);
                    }}
                    sx={{
                      opacity: 0.3,
                      '&:hover': { opacity: 1 },
                      color: isActive ? 'inherit' : 'text.secondary',
                    }}
                  >
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Project name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim() && newWorkspaceId) createMutation.mutate(newName.trim());
            }}
          />
          <Box sx={{ mt: 2 }}><WorkspaceField value={newWorkspaceId} onChange={setNewWorkspaceId} /></Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate(newName.trim())}
            disabled={!newName.trim() || !newWorkspaceId || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete project?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete the project and all its assets.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
